import { useMemo, useState } from "react"
import {
    Autocomplete,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { addContentLanguage } from "./controller/add-content-language"
import { getContentLanguages } from "./controller/get-content-languages"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { LANGUAGES } from "./languages"

export function AddLanguageDialog({ ok, cancel }) {
    const [selected, setSelected] = useState(null)
    const [saving, setSaving] = useState(false)
    const rawExisting = getContentLanguages.useResults(withInitialValue(IS_LOADING))
    const existingLanguages = rawExisting === IS_LOADING ? [] : rawExisting ?? []

    const availableLanguages = useMemo(filterAvailable, [existingLanguages])

    return (
        <>
            <DialogTitle>Add Content Language</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
                    <Autocomplete
                        options={availableLanguages}
                        value={selected}
                        onChange={handleSelect}
                        getOptionLabel={getOptionLabel}
                        renderOption={renderOption}
                        renderInput={renderInput}
                        filterOptions={filterOptions}
                        isOptionEqualToValue={isOptionEqual}
                        size="small"
                        autoHighlight
                    />
                    <If value={selected}>
                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Code: <strong>{selected?.code}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Native name: <strong>{selected?.nativeName}</strong>
                            </Typography>
                            <If value={selected?.rtl}>
                                <Typography variant="body2" color="text.secondary">
                                    Right-to-left (RTL)
                                </Typography>
                            </If>
                        </Stack>
                    </If>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!selected || saving}>
                    Add Language
                </Button>
            </DialogActions>
        </>
    )

    function filterAvailable() {
        const existingCodes = new Set(existingLanguages.map((l) => l.code))
        return LANGUAGES.filter((l) => !existingCodes.has(l.code))
    }

    function handleSelect(_, value) {
        setSelected(value)
    }

    function handleCancel() {
        cancel()
    }

    async function handleSave() {
        if (!selected) return
        setSaving(true)
        try {
            const result = await addContentLanguage({
                code: selected.code,
                name: selected.name,
                nativeName: selected.nativeName,
                rtl: selected.rtl ?? false,
            })
            successSnackbar(`${selected.name} added as a content language`)
            ok(result)
        } catch (e) {
            errorSnackbar("Failed to add language")
            setSaving(false)
        }
    }
}

function getOptionLabel(option) {
    return `${option.name} (${option.nativeName})`
}

function renderOption(props, option) {
    return (
        <li {...props} key={option.code}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {option.nativeName}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ ml: "auto !important" }}>
                    {option.code}
                </Typography>
            </Stack>
        </li>
    )
}

function renderInput(params) {
    return <TextField {...params} label="Select Language" placeholder="Search languages..." />
}

function filterOptions(options, { inputValue }) {
    const search = inputValue.toLowerCase()
    return options.filter(
        (o) =>
            o.name.toLowerCase().includes(search) ||
            o.nativeName.toLowerCase().includes(search) ||
            o.code.toLowerCase().includes(search)
    )
}

function isOptionEqual(option, value) {
    return option.code === value.code
}
